import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { queryClient } from '@/lib/query-client'
import { AuthProvider } from '@/contexts/auth-context'
import { ThemeProvider } from '@/contexts/theme-context'
import { AppLayout, AuthLayout } from '@/components/layout'
import { ProtectedRoute, RedirectIfAuthenticated } from '@/components/auth'
import {
  LoginPage,
  SignUpPage,
  SignUpSuccessPage,
  ConfirmEmailPage,
  EmailNotVerifiedPage,
  DashboardPage,
  ChatPage,
  ChatDetailPage,
  AISettingsPage,
  ChannelsPage,
  ConversationsPage,
  WebhookLogsPage,
  AttendanceMetricsPage,
  AttendanceMetricsManagementPage,
  SnapshotsListPage,
  SnapshotDashboardPage,
  KnowledgeListPage,
  KnowledgeViewPage,
  KnowledgeEditPage,
  KnowledgeVersionsPage,
  KnowledgeVersionViewPage,
  KnowledgeVersionComparePage,
  KnowledgeExtractionSettingsPage,
  KnowledgeExtractionLogsPage,
  KnowledgeExtractionLogDetailPage,
} from '@/pages'

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Auth routes - Redirect if authenticated */}
            <Route element={
              <RedirectIfAuthenticated>
                <AuthLayout />
              </RedirectIfAuthenticated>
            }>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/signup/success" element={<SignUpSuccessPage />} />
            </Route>

            {/* Email verification routes - No auth redirect */}
            <Route element={<AuthLayout />}>
              <Route path="/confirm-email" element={<ConfirmEmailPage />} />
              <Route path="/email-not-verified" element={<EmailNotVerifiedPage />} />
            </Route>

            {/* Protected routes */}
            <Route element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/chat/:threadId" element={<ChatDetailPage />} />
              <Route path="/channels" element={<ChannelsPage />} />
              <Route path="/conversations" element={<ConversationsPage />} />
              <Route path="/webhook-logs" element={<WebhookLogsPage />} />
              <Route path="/attendance-metrics" element={<AttendanceMetricsPage />} />
              <Route path="/attendance-metrics/snapshots" element={<SnapshotsListPage />} />
              <Route path="/attendance-metrics/snapshot/:id" element={<SnapshotDashboardPage />} />
              <Route path="/attendance-metrics/management" element={<AttendanceMetricsManagementPage />} />
              <Route path="/settings/my-iablue" element={<AISettingsPage />} />
              <Route path="/knowledge" element={<KnowledgeListPage />} />
              <Route path="/knowledge/:id" element={<KnowledgeViewPage />} />
              <Route path="/knowledge/:id/edit" element={<KnowledgeEditPage />} />
              <Route path="/knowledge/:id/versions" element={<KnowledgeVersionsPage />} />
              <Route path="/knowledge/:id/versions/compare" element={<KnowledgeVersionComparePage />} />
              <Route path="/knowledge/:id/versions/:versionId" element={<KnowledgeVersionViewPage />} />
              <Route path="/settings/knowledge-extraction" element={<KnowledgeExtractionSettingsPage />} />
              <Route path="/settings/knowledge-extraction/logs" element={<KnowledgeExtractionLogsPage />} />
              <Route path="/settings/knowledge-extraction/logs/:id" element={<KnowledgeExtractionLogDetailPage />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>

            <Toaster
              position="top-center"
              expand={false}
              richColors
              closeButton
              toastOptions={{
                duration: 4000,
                classNames: {
                  toast: 'w-full sm:w-auto sm:min-w-[420px] sm:max-w-[520px]',
                  title: 'text-sm font-semibold',
                  description: 'text-sm',
                  error: 'border-destructive',
                  success: 'border-green-500',
                },
              }}
            />
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
